import { useEffect, useMemo, useState } from 'react';
import { adminApi, ApiError } from '../lib/api';
import type {
  DecodedTranscript,
  PaperEditAnalysis,
  PaperEditStatus,
  QuestionMarker,
  Recording,
  RecordingDetail,
  TemplateKey,
} from '../../shared/types';
import type { InterviewQuestion } from '../../shared/extractInterviewQuestions';
import {
  buildPaperEditMarkdown,
  buildPaperEditSegments,
  formatTimecode,
} from '../../shared/buildPaperEdit';
import Eyebrow from './Eyebrow';
import Button from './Button';

interface Props {
  open: boolean;
  onClose: () => void;
  recording: Pick<Recording, 'id' | 'duration_ms' | 'recorded_at'>;
  takeNumber: number;
  transcript: DecodedTranscript;
  markers: QuestionMarker[];
  questions: InterviewQuestion[];
  scriptTitle: string;
  clientName: string;
  /** The script's authored template_key — the default lens the AI used
   *  on first analysis. */
  scriptTemplateKey: TemplateKey;
  /** Latest paper-edit state pulled from the recording detail row. The
   *  dialog also polls internally while analysing/regenerating. */
  paperEditStatus: PaperEditStatus;
  paperEditJson: string | null;
  paperEditError: string | null;
  paperEditTemplateOverride: string | null;
  /** Called after a regenerate request succeeds so the parent panel can
   *  re-fetch and reflect the new analysis once it's ready. */
  onDetailRefresh: () => Promise<RecordingDetail | null>;
}

type ViewMode = 'paper-edit' | 'by-question' | 'plain';

const TEMPLATE_LABELS: Record<TemplateKey, string> = {
  testimonial: 'Testimonial',
  brand_film: 'Brand film',
  social_short: 'Social short',
  fb_ad_campaign: 'Paid ad',
  explainer: 'Explainer',
  interview_brief: 'Interview brief',
};

const TEMPLATE_OPTIONS: TemplateKey[] = [
  'testimonial',
  'brand_film',
  'social_short',
  'fb_ad_campaign',
  'explainer',
  'interview_brief',
];

/**
 * Full-screen modal for reading a long transcript at a comfortable
 * width — the sidebar panel is too narrow to read 4000+ words in. Shows
 * the take's text either segmented by question (using the markers tapped
 * during recording + Whisper's word timestamps) or as a single block of
 * prose, and offers Copy + Download .md for paper-edit export.
 *
 * Matches VersionHistoryPanel's overlay treatment so the admin app feels
 * coherent: dimmed backdrop, slide-in panel from the right, Esc closes,
 * stopPropagation on the panel itself.
 */
export default function TranscriptDialog({
  open,
  onClose,
  recording,
  takeNumber,
  transcript,
  markers,
  questions,
  scriptTitle,
  clientName,
  scriptTemplateKey,
  paperEditStatus,
  paperEditJson,
  paperEditError,
  paperEditTemplateOverride,
  onDetailRefresh,
}: Props) {
  // Default view priority: paper edit when ready, else question segments
  // when markers exist, else plain text. Honest fallback so the dialog
  // always opens to the most useful tab for the data we have.
  const [view, setView] = useState<ViewMode>(() => {
    if (paperEditStatus === 'ready') return 'paper-edit';
    return markers.length > 0 ? 'by-question' : 'plain';
  });
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // Local mirror so we keep updating the UI as the polling loop refreshes
  // the underlying recording row.
  const [localStatus, setLocalStatus] = useState<PaperEditStatus>(paperEditStatus);
  const [localJson, setLocalJson] = useState<string | null>(paperEditJson);
  const [localError, setLocalError] = useState<string | null>(paperEditError);
  const [override, setOverride] = useState<TemplateKey | null>(
    (paperEditTemplateOverride as TemplateKey | null) ?? null,
  );
  // The template the most recent generation actually ran under — drives
  // the "currently analysed as: X" label distinct from the override
  // dropdown's value (which represents the user's intent for the NEXT
  // regenerate click).
  const analysedAs: TemplateKey =
    (override as TemplateKey | null) ?? scriptTemplateKey;

  // Build segments + markdown once per (recording, view-affecting input)
  // tuple. Cheap enough that we don't really need memoisation, but it
  // avoids re-running for every render while the modal is open.
  const segments = useMemo(
    () =>
      buildPaperEditSegments({
        transcript,
        markers,
        questions,
        recording,
      }),
    [transcript, markers, questions, recording],
  );

  const markdown = useMemo(
    () =>
      buildPaperEditMarkdown({
        transcript,
        markers,
        questions,
        recording,
        title: scriptTitle,
        clientName,
        takeNumber,
      }),
    [transcript, markers, questions, recording, scriptTitle, clientName, takeNumber],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset transient "Copied!" affordance whenever the dialog reopens.
  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  // Keep local state in sync if the parent re-fetches the row (e.g. its
  // polling loop detects the analysis finished).
  useEffect(() => {
    setLocalStatus(paperEditStatus);
    setLocalJson(paperEditJson);
    setLocalError(paperEditError);
  }, [paperEditStatus, paperEditJson, paperEditError]);

  // Auto-poll while paper edit is in flight. Stops the moment it's
  // terminal so we're not wasting requests on a stable row.
  useEffect(() => {
    if (!open) return;
    if (localStatus !== 'analysing') return;
    let cancelled = false;
    const tick = async () => {
      const detail = await onDetailRefresh();
      if (cancelled || !detail) return;
      setLocalStatus(detail.paper_edit_status);
      setLocalJson(detail.paper_edit_json);
      setLocalError(detail.paper_edit_error);
    };
    const handle = window.setInterval(() => void tick(), 3000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, localStatus, onDetailRefresh]);

  // Snap the visible tab to the new "ready" content as soon as
  // generation finishes, so the user lands on the freshly-produced
  // paper edit without an extra click.
  useEffect(() => {
    if (localStatus === 'ready' && view !== 'paper-edit') {
      setView('paper-edit');
    }
    // intentionally only watches status flipping to ready
  }, [localStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const hasMarkers = markers.length > 0;
  const analysis = decodePaperEdit(localJson);

  async function handleRegenerate() {
    if (regenerating) return;
    setRegenerating(true);
    setLocalError(null);
    try {
      await adminApi.regeneratePaperEdit(recording.id, override);
      setLocalStatus('analysing');
      // Polling effect above takes over from here.
    } catch (err) {
      setLocalError(err instanceof ApiError ? err.message : 'Regenerate failed.');
    } finally {
      setRegenerating(false);
    }
  }
  // Switching back to segmented is only useful when markers exist;
  // otherwise the segmented and plain views render the same content.

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Some browsers (notably iOS Safari in privacy modes) refuse
      // navigator.clipboard. Silently fall back to a selection prompt.
      const tmp = document.createElement('textarea');
      tmp.value = markdown;
      document.body.appendChild(tmp);
      tmp.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      } catch {
        // truly nothing we can do — user can still use Download.
      }
      tmp.remove();
    }
  }

  function handleDownload() {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const safeName = `${sanitize(clientName)} — ${sanitize(scriptTitle)} — take ${takeNumber}.md`;
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
  }

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md z-50 animate-fade-in flex items-stretch justify-end"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="
          w-full max-w-4xl bg-page border-l border-line
          flex flex-col animate-rise-subtle
        "
        style={{ animationDuration: '300ms' }}
      >
        <header className="px-6 py-4 border-b border-line flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Eyebrow accent>// Transcript · Take {takeNumber}</Eyebrow>
            <h2 className="font-display font-bold text-[22px] tracking-[-0.01em] mt-1.5 truncate">
              {scriptTitle}
            </h2>
            <p className="text-faint text-[12px] font-mono mt-1">
              {formatRecordedAt(recording.recorded_at)} ·{' '}
              {formatTimecode((recording.duration_ms ?? 0) / 1000)} ·{' '}
              {wordCount(transcript)} words
            </p>
          </div>
          <button
            type="button"
            autoFocus
            onClick={onClose}
            aria-label="Close"
            className="text-muted hover:text-fg transition-colors text-xl leading-none px-2"
          >
            ×
          </button>
        </header>

        <div className="px-6 py-3 border-b border-line flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center bg-card border border-line rounded-md p-0.5">
            <TabButton
              active={view === 'paper-edit'}
              onClick={() => setView('paper-edit')}
            >
              Paper edit
            </TabButton>
            {hasMarkers && (
              <TabButton
                active={view === 'by-question'}
                onClick={() => setView('by-question')}
              >
                By question
              </TabButton>
            )}
            <TabButton
              active={view === 'plain'}
              onClick={() => setView('plain')}
            >
              Plain text
            </TabButton>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleCopy}>
              {copied ? '✓ Copied' : 'Copy paper edit'}
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownload}>
              Download .md
            </Button>
          </div>
        </div>

        {/* Paper-edit toolbar — only relevant on the Paper edit tab.
            Other tabs hide it to keep their chrome quieter. */}
        {view === 'paper-edit' && (
          <div className="px-6 py-2.5 border-b border-line/70 bg-card/40 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                Analyse as
              </span>
              <select
                value={override ?? scriptTemplateKey}
                onChange={(e) => {
                  const next = e.target.value as TemplateKey;
                  setOverride(next === scriptTemplateKey ? null : next);
                }}
                className="
                  font-mono text-[11px] uppercase tracking-[0.10em]
                  bg-page border border-line rounded px-2 py-1
                  text-fg hover:border-accent transition-colors
                "
              >
                {TEMPLATE_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {TEMPLATE_LABELS[k]}
                    {k === scriptTemplateKey ? ' (script default)' : ''}
                  </option>
                ))}
              </select>
              {analysis && analysis.templateKey !== analysedAs && (
                <span className="font-mono text-[10px] text-faint">
                  · current: {TEMPLATE_LABELS[analysis.templateKey]}
                </span>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRegenerate}
              // Allowed to click even when status is 'analysing' so the
              // user can rescue a stuck row (where a previous run died
              // mid-call). The endpoint is idempotent — re-claiming an
              // 'analysing' row just kicks off a fresh background gen.
              disabled={regenerating}
            >
              {regenerating
                ? 'Starting…'
                : localStatus === 'analysing'
                  ? 'Analysing… (click to retry)'
                  : '↻ Regenerate'}
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-8 py-8">
          {view === 'paper-edit' ? (
            <PaperEditView
              status={localStatus}
              analysis={analysis}
              error={localError}
              questions={questions}
            />
          ) : view === 'by-question' && hasMarkers ? (
            <SegmentedView segments={segments} />
          ) : (
            <PlainView text={transcript.text} />
          )}
        </div>
      </aside>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 rounded transition-colors ' +
        (active ? 'bg-fg text-page' : 'text-muted hover:text-fg')
      }
    >
      {children}
    </button>
  );
}

function PaperEditView({
  status,
  analysis,
  error,
  questions,
}: {
  status: PaperEditStatus;
  analysis: PaperEditAnalysis | null;
  error: string | null;
  questions: InterviewQuestion[];
}) {
  if (status === 'pending') {
    return (
      <CenteredMessage
        eyebrow="Paper edit"
        title="Waiting for transcription"
        body="The paper edit auto-generates after Whisper finishes transcribing this take."
      />
    );
  }
  if (status === 'analysing') {
    return (
      <CenteredMessage
        eyebrow="Generating"
        title="Reading the transcript…"
        body="The AI is pulling hooks, key points, closings, and standout soundbites. This usually takes 10–30 seconds."
        pulse
      />
    );
  }
  if (status === 'failed') {
    return (
      <CenteredMessage
        eyebrow="Failed"
        title="Couldn't generate the paper edit"
        body={
          error ??
          'The model returned an unexpected response. Tap Regenerate to try again.'
        }
        accent
      />
    );
  }
  if (!analysis) {
    return (
      <CenteredMessage
        eyebrow="Paper edit"
        title="No analysis available"
        body="Tap Regenerate above to produce one."
      />
    );
  }

  return (
    <div className="max-w-2xl space-y-10">
      <PaperEditSection
        title="Suggested hooks"
        subtitle="Opening lines that match the video type"
        items={analysis.hooks}
      />
      <KeyPointsSection keyPoints={analysis.keyPoints} questions={questions} />
      <PaperEditSection
        title="Closings & CTAs"
        subtitle="Lines that work as a strong ending or call-to-action"
        items={analysis.closings}
      />
      <PaperEditSection
        title="Standout soundbites"
        subtitle="Quotable moments worth pulling for client previews"
        items={analysis.soundbites}
      />
    </div>
  );
}

function PaperEditSection({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle: string;
  items: Array<{ quote: string; rationale: string }>;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <div className="mb-3">
        <Eyebrow accent>// {title}</Eyebrow>
        <p className="text-faint text-[12px] mt-1">{subtitle}</p>
      </div>
      <ul className="space-y-4">
        {items.map((item, i) => (
          <li key={i} className="border-l-2 border-accent/50 pl-4">
            <p className="text-fg text-[16px] leading-[1.55] italic">
              “{item.quote}”
            </p>
            <p className="text-muted text-[12px] mt-1.5">{item.rationale}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function KeyPointsSection({
  keyPoints,
  questions,
}: {
  keyPoints: PaperEditAnalysis['keyPoints'];
  questions: InterviewQuestion[];
}) {
  if (keyPoints.length === 0) return null;
  return (
    <section>
      <div className="mb-3">
        <Eyebrow accent>// Key points by question</Eyebrow>
        <p className="text-faint text-[12px] mt-1">
          The talent's answers distilled to bullets per question
        </p>
      </div>
      <div className="space-y-5">
        {keyPoints.map((kp, i) => {
          const heading =
            kp.questionIndex !== null
              ? `Q${kp.questionIndex + 1}. ${
                  questions[kp.questionIndex]?.text ?? kp.questionText
                }`
              : kp.questionText;
          return (
            <div key={i}>
              <h4 className="font-display font-semibold text-[15px] text-fg mb-2">
                {heading}
              </h4>
              <ul className="space-y-1.5 list-disc pl-5">
                {kp.points.map((pt, j) => (
                  <li key={j} className="text-fg text-[14px] leading-[1.55]">
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CenteredMessage({
  eyebrow,
  title,
  body,
  pulse,
  accent,
}: {
  eyebrow: string;
  title: string;
  body: string;
  pulse?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="max-w-md mx-auto text-center py-16">
      <p
        className={
          'font-mono text-[11px] uppercase tracking-[0.12em] mb-3 flex items-center justify-center gap-2 ' +
          (accent ? 'text-accent' : 'text-faint')
        }
      >
        {pulse && (
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        )}
        // {eyebrow}
      </p>
      <h3 className="font-display font-bold text-[20px] text-fg mb-2">
        {title}
      </h3>
      <p className="text-muted text-[14px] leading-relaxed">{body}</p>
    </div>
  );
}

function decodePaperEdit(json: string | null): PaperEditAnalysis | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as PaperEditAnalysis;
    if (
      !Array.isArray(parsed.hooks) ||
      !Array.isArray(parsed.keyPoints) ||
      !Array.isArray(parsed.closings) ||
      !Array.isArray(parsed.soundbites)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function SegmentedView({
  segments,
}: {
  segments: ReturnType<typeof buildPaperEditSegments>;
}) {
  return (
    <div className="max-w-2xl space-y-8">
      {segments.map((seg, i) => (
        <section key={i}>
          <div className="flex items-baseline justify-between gap-3 mb-1.5">
            <h3 className="font-display font-bold text-[17px] tracking-[-0.01em] text-fg">
              {seg.questionIndex !== null
                ? `Q${seg.questionIndex + 1}. ${seg.questionText}`
                : seg.questionText}
            </h3>
            <span className="font-mono text-[10px] tabular-nums text-faint whitespace-nowrap">
              {formatTimecode(seg.startS)} — {formatTimecode(seg.endS)}
            </span>
          </div>
          <p className="text-fg text-[15px] leading-[1.65]">{seg.text}</p>
        </section>
      ))}
    </div>
  );
}

function PlainView({ text }: { text: string }) {
  return (
    <div className="max-w-2xl">
      <p className="text-fg text-[15px] leading-[1.7] whitespace-pre-wrap">
        {text}
      </p>
    </div>
  );
}

// ---------- Helpers ----------

function wordCount(t: DecodedTranscript): number {
  if (typeof t.word_count === 'number' && t.word_count > 0) return t.word_count;
  return t.text.trim().split(/\s+/).filter(Boolean).length;
}

function formatRecordedAt(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function sanitize(s: string): string {
  return s.replace(/[\\/:*?"<>|]/g, '').trim() || 'Transcript';
}
